import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle } from 'lucide-react';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../lib/analytics';
import { cabinetPath } from '../lib/domains';

interface AdminAuthPageProps {
    mode: 'login' | 'register';
}

export const AdminAuthPage: React.FC<AdminAuthPageProps> = ({ mode }) => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '',
    });
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const { login } = useAdminAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || cabinetPath('admin', '/dashboard');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            if (mode === 'login') {
                await login({
                    username: formData.username,
                    password: formData.password,
                });
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SUCCEEDED, {
                    user_type: 'admin',
                    auth_mode: 'login',
                });
                navigate(from, { replace: true });
            } else {
                setError("Регистрация администратора через UI закрыта.");
            }
        } catch (err: any) {
            if (err.response?.data?.detail) {
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_FAILED, {
                    user_type: 'admin',
                    auth_mode: mode,
                    error_message: err.response.data.detail,
                });
                setError(err.response.data.detail);
            } else {
                const message = 'Произошла ошибка. Попробуйте позже.';
                trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_FAILED, {
                    user_type: 'admin',
                    auth_mode: mode,
                    error_message: message,
                });
                setError(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
            <Card className="w-full max-w-md shadow-lg border-t-4 border-t-red-600">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                        {mode === 'login' ? 'Вход для администратора' : 'Регистрация администратора'}
                    </CardTitle>
                    <CardDescription>
                        {mode === 'login'
                            ? 'Доступ к главному пульту управления'
                            : 'Создание аккаунта главного администратора'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md flex items-center gap-2 text-red-600 dark:text-red-300 text-sm">
                            <AlertCircle className="h-4 w-4" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username">Имя пользователя</Label>
                            <Input
                                id="username"
                                name="username"
                                type="text"
                                placeholder="admin"
                                required
                                value={formData.username}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Пароль</Label>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                placeholder="••••••••"
                                required
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full bg-red-600 hover:bg-red-700 text-white"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Загрузка...' : (mode === 'login' ? 'Войти' : 'Зарегистрироваться')}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};
